import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { canIvrHub } from '../utils/permissions';
import {
  ivrMeta,
  ivrGetCallLogs,
  ivrGetClickToCallLogs,
  ivrGetDialerLogs,
  ivrGetClickConnectLogs,
  ivrGetAllCallLogsRecent,
  ivrClickConnect,
  ivrClickConnectUpdate,
  ivrEnableDisableAgent,
  ivrAddAgent,
  ivrUpdateAgentDid,
  ivrC2cAdvance,
  ivrC2cLogsByNumber,
  ivrCallLogsByNumber,
} from '../api/ivrApi';
import {
  IVR_DEFAULT_DID,
  getIvrPrefs,
  setIvrPrefs,
  normalizeIvrPhone,
} from '../config/ivr';
import IvrClickToCall from '../components/IvrClickToCall';

const todayYmd = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const daysAgoYmd = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Log source filters mapped to IVR endpoints */
const LOG_TYPES = [
  { key: 'inbound', label: 'Inbound / IVR', hint: 'DID incoming & IVR flow calls' },
  { key: 'dialer', label: 'Dialer', hint: 'Auto-dialer / campaign outbound' },
  { key: 'masking', label: 'Number masking', hint: 'Click-Connect masked sessions' },
  { key: 'all_recent', label: 'All recent', hint: 'Mixed recent inbound + C2C + dialer' },
  { key: 'c2c', label: 'Click-to-call', hint: 'Agent ↔ customer bridges (what admin dialer uses)' },
];

const logTypeLabel = (row) => {
  if (row._log_source === 'c2c' || row.call_type === 'c2c') return 'C2C';
  if (row._log_source === 'dialer') return 'Dialer';
  if (row._log_source === 'masking') return 'Masking';
  if (row._log_source === 'inbound' || row.call_type === 'inbound') return 'Inbound';
  if (row.call_type) return String(row.call_type);
  return '–';
};

const IvrPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [dids, setDids] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logType, setLogType] = useState('inbound');
  const [fromDate, setFromDate] = useState(daysAgoYmd(7));
  const [toDate, setToDate] = useState(todayYmd());
  const [prefs, setPrefsState] = useState(getIvrPrefs());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Masking
  const [maskFirst, setMaskFirst] = useState('');
  const [maskSecond, setMaskSecond] = useState('');
  const [maskDid, setMaskDid] = useState(IVR_DEFAULT_DID);
  const [maskBusy, setMaskBusy] = useState(false);

  // Advanced C2C
  const [advFirst, setAdvFirst] = useState('');
  const [advSecond, setAdvSecond] = useState('');
  const [advDid, setAdvDid] = useState(IVR_DEFAULT_DID);
  const [advBody, setAdvBody] = useState('');
  const [advBusy, setAdvBusy] = useState(false);

  // Lookup by number
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupMode, setLookupMode] = useState('c2c'); // c2c | inbound

  // Add agent dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    phonenumber: '',
    password: '',
  });
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    if (!canIvrHub(user)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const toast = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadAgentsAndDids = useCallback(async () => {
    setLoading(true);
    const res = await ivrMeta();
    setLoading(false);
    if (!res.success) {
      toast(res.error || 'Failed to load IVR meta', 'error');
      setAgents([]);
      setDids([IVR_DEFAULT_DID]);
      return;
    }
    setAgents(res.agents || []);
    const list = (res.dids || []).filter(Boolean);
    setDids(list.length ? list : [res.default_did || IVR_DEFAULT_DID]);
    const preferred = res.default_did || list[0];
    if (preferred) {
      if (!maskDid) setMaskDid(preferred);
      if (!advDid) setAdvDid(preferred);
    }
  }, [maskDid, advDid]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    let res;
    const range = { from_date: fromDate, to_date: toDate, orderby: 'desc' };

    if (logType === 'c2c') {
      res = await ivrGetClickToCallLogs(range);
      if (res.success) {
        res = {
          ...res,
          logs: (res.logs || []).map((r) => ({ ...r, _log_source: 'c2c' })),
        };
      }
    } else if (logType === 'inbound') {
      res = await ivrGetCallLogs(range);
      if (res.success) {
        res = {
          ...res,
          logs: (res.logs || []).map((r) => ({ ...r, _log_source: r.call_type || 'inbound' })),
        };
      }
    } else if (logType === 'dialer') {
      res = await ivrGetDialerLogs({ from_date: fromDate, to_date: toDate });
      if (res.success) {
        res = {
          ...res,
          logs: (res.logs || []).map((r) => ({ ...r, _log_source: 'dialer' })),
        };
      }
    } else if (logType === 'masking') {
      res = await ivrGetClickConnectLogs({
        did: maskDid || IVR_DEFAULT_DID,
        from_date: fromDate,
        to_date: toDate,
        orderby: 'desc',
      });
      if (res.success) {
        res = {
          ...res,
          logs: (res.logs || []).map((r) => ({ ...r, _log_source: 'masking' })),
        };
      }
    } else {
      res = await ivrGetAllCallLogsRecent();
    }

    setLoading(false);
    if (!res.success) {
      toast(res.error || 'Failed to load logs', 'error');
      setLogs([]);
      return;
    }
    setLogs(res.logs || []);
    if (!(res.logs || []).length) {
      toast(res.message || `No ${logType} logs found`, 'info');
    }
  }, [fromDate, toDate, logType, maskDid]);

  useEffect(() => {
    loadAgentsAndDids();
  }, [loadAgentsAndDids]);

  useEffect(() => {
    if (tab === 2) loadLogs();
  }, [tab, loadLogs]);

  const savePrefs = () => {
    const next = setIvrPrefs(prefs);
    setPrefsState(next);
    toast('Default agent / DID saved for click-to-call');
  };

  const handleToggleAgent = async (ext_no, enable) => {
    setLoading(true);
    const res = await ivrEnableDisableAgent(ext_no, enable ? '1' : '0');
    setLoading(false);
    if (!res.success) {
      toast(res.error || 'Failed', 'error');
      return;
    }
    toast(res.message || (enable ? 'Enabled' : 'Disabled'));
    loadAgentsAndDids();
  };

  const handleAssignDid = async (ext_no, did_num) => {
    setLoading(true);
    const res = await ivrUpdateAgentDid(ext_no, did_num);
    setLoading(false);
    if (!res.success) {
      toast(res.error || 'Failed to assign DID', 'error');
      return;
    }
    toast(res.message || 'DID assigned');
    loadAgentsAndDids();
  };

  const handleAddAgent = async () => {
    if (!addForm.name || !addForm.email || !addForm.phonenumber || !addForm.password) {
      toast('All agent fields are required', 'warning');
      return;
    }
    setAddBusy(true);
    const res = await ivrAddAgent(addForm);
    setAddBusy(false);
    if (!res.success) {
      toast(res.error || 'Failed to add agent', 'error');
      return;
    }
    toast(res.message || 'Agent added');
    setAddOpen(false);
    setAddForm({ name: '', email: '', phonenumber: '', password: '' });
    loadAgentsAndDids();
  };

  const handleMaskConnect = async () => {
    if (!normalizeIvrPhone(maskFirst) || !normalizeIvrPhone(maskSecond)) {
      toast('Enter both phone numbers', 'warning');
      return;
    }
    setMaskBusy(true);
    const res = await ivrClickConnect({
      first_phone: maskFirst,
      second_phone: maskSecond,
      did: maskDid,
      mode: 'single',
    });
    setMaskBusy(false);
    if (!res.success) {
      toast(res.error || 'Masked call failed', 'error');
      return;
    }
    toast(
      res.logid
        ? `${res.message || 'Connected'} (log #${res.logid})`
        : res.message || 'Masked call started'
    );
  };

  const handleMaskCancel = async () => {
    setMaskBusy(true);
    const res = await ivrClickConnectUpdate({
      action: 'cancel',
      did: maskDid,
      first_number: maskFirst,
    });
    setMaskBusy(false);
    if (!res.success) {
      toast(res.error || 'Cancel failed', 'error');
      return;
    }
    toast(res.message || 'Session cancelled');
  };

  const handleAdvCall = async () => {
    setAdvBusy(true);
    const res = await ivrC2cAdvance({
      did: advDid,
      first_phone: advFirst,
      second_phone: advSecond,
      body: advBody || undefined,
      timezone: 'Asia/Kolkata',
    });
    setAdvBusy(false);
    if (!res.success) {
      toast(res.error || 'Call failed', 'error');
      return;
    }
    toast(
      res.logid
        ? `${res.message || 'Connecting'} (log #${res.logid})`
        : res.message || 'Call connecting'
    );
  };

  const handleLookup = async () => {
    if (!normalizeIvrPhone(lookupPhone)) {
      toast('Enter phone', 'warning');
      return;
    }
    setLoading(true);
    const res =
      lookupMode === 'inbound'
        ? await ivrCallLogsByNumber(lookupPhone)
        : await ivrC2cLogsByNumber(lookupPhone);
    setLoading(false);
    if (!res.success) {
      toast(res.error || 'Lookup failed', 'error');
      setLogs([]);
      return;
    }
    const tagged = (res.logs || []).map((r) => ({
      ...r,
      _log_source: lookupMode === 'inbound' ? 'inbound' : 'c2c',
    }));
    setLogs(tagged);
    if (!tagged.length) {
      toast(`No ${lookupMode === 'inbound' ? 'inbound' : 'C2C'} logs for this number`, 'info');
    } else {
      toast(`Found ${tagged.length} log(s) for ${lookupPhone}`);
    }
  };

  if (!canIvrHub(user)) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {loading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }} />
      )}

      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate('/')}
            size="small"
            sx={{ color: '#FF7700', bgcolor: '#fff', border: '1px solid #e4e8ee' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#2E4057' }}>
              IVR Solutions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Superadmin · click-to-call, agents, DIDs, logs, number masking
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadAgentsAndDids}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>

        <Paper sx={{ borderRadius: 2.5, border: '1px solid #e4e8ee', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid #e4e8ee', px: 1 }}
          >
            <Tab icon={<PhoneIcon />} iconPosition="start" label="Defaults" sx={{ textTransform: 'none' }} />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Agents" sx={{ textTransform: 'none' }} />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Call logs" sx={{ textTransform: 'none' }} />
            <Tab icon={<LinkIcon />} iconPosition="start" label="Masking & advanced" sx={{ textTransform: 'none' }} />
          </Tabs>

          <Box sx={{ p: 2.5 }}>
            {tab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={700} gutterBottom>
                    Default click-to-call prefs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Used as the starting selection on Booking Details and header Call number.
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Default DID"
                    value={prefs.did || IVR_DEFAULT_DID}
                    onChange={(e) => setPrefsState((p) => ({ ...p, did: e.target.value }))}
                    sx={{ mb: 2 }}
                  >
                    {(dids.length ? dids : [IVR_DEFAULT_DID]).map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Default agent extension"
                    value={prefs.ext_no || ''}
                    onChange={(e) => setPrefsState((p) => ({ ...p, ext_no: e.target.value }))}
                    sx={{ mb: 2 }}
                  >
                    {agents.map((a) => (
                      <MenuItem key={a.ext_no} value={a.ext_no}>
                        {a.name} · {a.ext_no}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={savePrefs}
                    sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none' }}
                  >
                    Save defaults
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={700} gutterBottom>
                    Quick test call
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Account DID: <strong>{IVR_DEFAULT_DID}</strong> · {agents.length} agents
                  </Typography>
                  <TestCallRow />
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => setAddOpen(true)}
                    sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none' }}
                  >
                    Add agent
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Name', 'Ext', 'Phone', 'DID', 'Call', 'Actions'].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f5f7fa' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agents.map((a) => (
                        <TableRow key={a.ext_no} hover>
                          <TableCell>{a.name}</TableCell>
                          <TableCell>
                            <Chip size="small" label={a.ext_no} />
                          </TableCell>
                          <TableCell>{a.phone || '–'}</TableCell>
                          <TableCell>{a.did_num || '–'}</TableCell>
                          <TableCell>
                            <IvrClickToCall phone={a.phone} label="Call" size="small" />
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              label="Assign DID"
                              value={a.did_num || ''}
                              onChange={(e) => handleAssignDid(a.ext_no, e.target.value)}
                              sx={{ minWidth: 160, mr: 1 }}
                            >
                              {(dids.length ? dids : [IVR_DEFAULT_DID]).map((d) => (
                                <MenuItem key={d} value={d}>
                                  {d}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Button
                              size="small"
                              onClick={() => handleToggleAgent(a.ext_no, false)}
                              sx={{ textTransform: 'none', color: '#d32f2f' }}
                            >
                              Disable
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleToggleAgent(a.ext_no, true)}
                              sx={{ textTransform: 'none' }}
                            >
                              Enable
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!agents.length && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            No agents loaded
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tab === 2 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  IVR keeps different log streams. Pick a type — inbound alone will not show admin click-to-call
                  bridges.
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={logType}
                  onChange={(_, v) => v && setLogType(v)}
                  sx={{
                    mb: 2,
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1.5,
                    },
                  }}
                >
                  {LOG_TYPES.map((t) => (
                    <ToggleButton key={t.key} value={t.key}>
                      {t.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Alert severity="info" sx={{ mb: 2 }}>
                  {LOG_TYPES.find((t) => t.key === logType)?.hint}
                </Alert>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                  {logType !== 'all_recent' && (
                    <>
                      <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </>
                  )}
                  {logType === 'masking' && (
                    <TextField
                      select
                      size="small"
                      label="DID"
                      value={maskDid || IVR_DEFAULT_DID}
                      onChange={(e) => setMaskDid(e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {(dids.length ? dids : [IVR_DEFAULT_DID]).map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <Button
                    variant="contained"
                    onClick={loadLogs}
                    sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none' }}
                  >
                    Load logs
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <TextField
                    select
                    size="small"
                    label="Lookup type"
                    value={lookupMode}
                    onChange={(e) => setLookupMode(e.target.value)}
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="c2c">C2C by phone</MenuItem>
                    <MenuItem value="inbound">Inbound by phone</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    label="Phone"
                    value={lookupPhone}
                    onChange={(e) => setLookupPhone(e.target.value)}
                    placeholder="9876543210"
                  />
                  <Button onClick={handleLookup} sx={{ textTransform: 'none' }}>
                    Lookup
                  </Button>
                </Box>

                <Chip
                  size="small"
                  label={`${logs.length} row(s) · ${LOG_TYPES.find((t) => t.key === logType)?.label || logType}`}
                  sx={{ mb: 1.5, fontWeight: 600 }}
                />

                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          'Type',
                          'ID',
                          'Time',
                          'DID',
                          'Client',
                          'Agent',
                          'Ext / Dialer',
                          'Status',
                          'Duration',
                          'Recording',
                        ].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f5f7fa' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((row, idx) => (
                        <TableRow key={`${row.recordid || row.id || idx}-${idx}`} hover>
                          <TableCell>
                            <Chip size="small" label={logTypeLabel(row)} sx={{ height: 22, fontSize: 11 }} />
                          </TableCell>
                          <TableCell>{row.recordid || row.id || '–'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.call_time || row.datetime || '–'}
                          </TableCell>
                          <TableCell>{row.did_no || row.did || '–'}</TableCell>
                          <TableCell>
                            {row.client_no || row.customer_no || row.first_phone || row.second_phone || '–'}
                          </TableCell>
                          <TableCell>
                            {row.agent_name || row.agent_no || row.attended_by || '–'}
                          </TableCell>
                          <TableCell>
                            {row.ext_no ||
                              row.dialer_name ||
                              (row.dialer_id != null ? `dialer #${row.dialer_id}` : '–')}
                          </TableCell>
                          <TableCell>{row.call_status || row.status || '–'}</TableCell>
                          <TableCell>{row.call_duration ?? '–'}</TableCell>
                          <TableCell>
                            {row.recording_url ? (
                              <Button
                                size="small"
                                href={row.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textTransform: 'none' }}
                              >
                                Play
                              </Button>
                            ) : (
                              '–'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!logs.length && (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            No logs for this filter
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={700} gutterBottom>
                    Number masking (Click-Connect)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bridge two numbers over your DID so neither sees the other’s real number.
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="DID"
                    value={maskDid}
                    onChange={(e) => setMaskDid(e.target.value)}
                    sx={{ mb: 1.5 }}
                  >
                    {(dids.length ? dids : [IVR_DEFAULT_DID]).map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    label="First phone"
                    value={maskFirst}
                    onChange={(e) => setMaskFirst(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Second phone"
                    value={maskSecond}
                    onChange={(e) => setMaskSecond(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={maskBusy}
                      onClick={handleMaskConnect}
                      startIcon={maskBusy ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Connect masked
                    </Button>
                    <Button
                      disabled={maskBusy}
                      onClick={handleMaskCancel}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel session
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography fontWeight={700} gutterBottom>
                    Advanced click-to-call
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bridge first_phone → second_phone with optional whisper message.
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="DID"
                    value={advDid}
                    onChange={(e) => setAdvDid(e.target.value)}
                    sx={{ mb: 1.5 }}
                  >
                    {(dids.length ? dids : [IVR_DEFAULT_DID]).map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    label="First phone (rings first)"
                    value={advFirst}
                    onChange={(e) => setAdvFirst(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Second phone"
                    value={advSecond}
                    onChange={(e) => setAdvSecond(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Whisper / intro (optional)"
                    value={advBody}
                    onChange={(e) => setAdvBody(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Button
                    variant="contained"
                    disabled={advBusy}
                    onClick={handleAdvCall}
                    startIcon={advBusy ? <CircularProgress size={16} color="inherit" /> : <PhoneIcon />}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#FF7700',
                      '&:hover': { bgcolor: '#e65100' },
                    }}
                  >
                    Start advanced call
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={addOpen} onClose={() => !addBusy && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add IVR agent</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {['name', 'email', 'phonenumber', 'password'].map((field) => (
              <Grid item xs={12} sm={6} key={field}>
                <TextField
                  fullWidth
                  required
                  type={field === 'password' ? 'password' : 'text'}
                  label={
                    field === 'phonenumber'
                      ? 'Phone (10 digits)'
                      : field.charAt(0).toUpperCase() + field.slice(1)
                  }
                  value={addForm[field]}
                  onChange={(e) => setAddForm((f) => ({ ...f, [field]: e.target.value }))}
                />
              </Grid>
            ))}
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Password must include upper, lower, number and special character (IVR API rule).
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAgent}
            disabled={addBusy}
            sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}
          >
            {addBusy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function TestCallRow() {
  const [phone, setPhone] = useState('');
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
      <TextField
        size="small"
        label="Phone to call"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        sx={{ width: 200 }}
      />
      <IvrClickToCall phone={phone} label="Test call" />
    </Box>
  );
}

export default IvrPanel;
